/** @graphql agg **/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';

import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	buildContentAri,
	getContentGraphNodeLabel,
	type BylineContentType,
} from '../utils/contentAri.ts';
import { createLinkDeduper } from '../utils/createLinkDeduper.ts';
import {
	classifyLinkedJiraWorkItemsError,
	fetchLinkedJiraWorkItemUrls,
	isLinkedJiraWorkItemsClientError,
} from '../utils/fetchLinkedJiraWorkItemUrls.ts';
import { hydrateIncomingLinks } from '../utils/hydrateIncomingLinks.ts';
import { readIncomingLinkUrls } from '../utils/readIncomingLinkUrls.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import type {
	useIncomingLinksQuery$data,
	useIncomingLinksQuery$variables,
} from './__generated__/useIncomingLinksQuery.graphql.ts';
import {
	getIncomingLinksFailedEvent,
	getIncomingLinksLoadMoreSucceededEvent,
	getIncomingLinksRemoteLinksFailedEvent,
	getIncomingLinksRemoteLinksSucceededEvent,
	getIncomingLinksSucceededEvent,
} from './incomingLinksAnalytics.ts';

// A full batch up front: many incoming rows are dropped before they can be
// rendered (an entity type the catalog does not cover, a null url, or a url ORS
// has no provider for), so a small page can resolve to an empty section. 50 also
// matches link-provider's per-hostname ORS batch size, so a page's urls are
// resolved in as few requests as the client allows.
export const DEFAULT_INITIAL_PAGE_SIZE = 50;
export const DEFAULT_LOAD_MORE_PAGE_SIZE = 50;
const INITIAL_RESOLVED_LINK_TARGET = 3;

// Bounds the best-effort Jira remote-link fallback so a stuck app-link call is reported as a timeout
// instead of merging minutes later.
const REMOTE_LINKS_TIMEOUT_MS = 5000;

// Classify by the error's contents rather than the code path: an Apollo error
// with only a networkError is transport-level; one carrying graphQLErrors is a
// query/data failure. Anything unrecognised is treated as a network error.
const classifyError = (err: ApolloErrorInterop): 'network' | 'graphql' =>
	!err.networkError && err.graphQLErrors?.length ? 'graphql' : 'network';

// Upper bound on the rows the graph will match; 1000 is the AGG cypherQueryV2
// max. Pagination (`first`/`after`) walks this matched set a page at a time.
const MAX_INCOMING_LINKS = 1000;

// `(source)-[:content_referenced_entity]->(content:ConfluencePage)`, or `ConfluenceBlogpost`
// for a blog post: any-product or third-party entity that references this content.
// Confirmed against real TWG data by the [Spike] 3P Incoming Links investigation. Pages
// and blog posts are distinct graph node types, so the label and ARI both come from the
// content type (see `contentAri.ts`).
const LINKS_RELATIONSHIP = 'content_referenced_entity';

// AGG requires an `X-Query-Context` header naming the site (or workspace) ARI for
// `graphStore_*` fields, otherwise the gateway rejects the request with
// "Query context must not be null". The site ARI is derived from the cloudId.
const buildQueryContext = (cloudId: string): string => `ari:cloud:platform::site/${cloudId}`;

// `ORDER BY` puts the most recently updated referencing entities on page 1, which
// is all either surface shows: the byline's top-3 and the section's first page both
// stop well short of the 1000 matched rows.
//
// `lastUpdated` resolves against the *graph* node types this relationship can bind
// (`JiraIssue`, `GraphPullRequest`, ...), not the AGG types in the `data` union
// below -- the two namespaces disagree, e.g. `ExternalPullRequest` carries
// `lastUpdate` and no `lastUpdated`. No `content-referenced-entity` source type
// opts out of `lastUpdated`, but 32 node types do, so check
// `graph-catalogue/src/data/nodes.ts` (not the AGG schema) before ordering on any
// other property. Do not reach for `coalesce()` to bridge the namespaces: the
// gateway rejects the whole query.
const buildIncomingLinksCypher = (contentAri: string, contentType?: BylineContentType): string => {
	const label = getContentGraphNodeLabel(contentType);
	return [
		`MATCH (source)-[:${LINKS_RELATIONSHIP}]->(content:${label} {ari: "${contentAri}"})`,
		`RETURN source`,
		`ORDER BY source.lastUpdated DESC`,
		`LIMIT ${MAX_INCOMING_LINKS}`,
	].join('\n');
};

// The preload uses this query too, so the browser can reuse the complete first page.
const IncomingLinksQuery = graphql`
	query useIncomingLinksQuery($cypherQuery: String!, $first: Int, $after: String) {
		graphStore_cypherQueryV2(query: $cypherQuery, first: $first, after: $after)
			@optIn(to: "GraphStoreCypherQueryV2") {
			pageInfo {
				hasNextPage
				endCursor
			}
			edges {
				node {
					columns {
						key
						value {
							__typename
							... on GraphStoreCypherQueryV2AriNode {
								id
								data {
									# The per-type URL selection, shared with useRelatedContentQuery; defined in incomingLinkTypes.ts.
									...incomingLinkTypes_node @relay(mask: false)
								}
							}
						}
					}
				}
			}
		}
	}
`;

type RemoteLinksResult =
	| { status: 'resolved'; fetched: number; links: EnhancedSmartLink[] }
	| { status: 'failed' };

// Appends the links not already present — by `createLinkDeduper`, the same identity
// `hydrateIncomingLinks` dedupes on — re-indexed so `occurrenceIndex` stays the global position and
// recency order is preserved across pages.
const appendNewLinks = (
	prev: EnhancedSmartLink[],
	incoming: EnhancedSmartLink[],
): EnhancedSmartLink[] => {
	const isDuplicate = createLinkDeduper(prev);
	const fresh: EnhancedSmartLink[] = [];
	incoming.forEach((link) => {
		if (isDuplicate(link)) {
			return;
		}
		fresh.push({ ...link, occurrenceIndex: prev.length + fresh.length });
	});
	return fresh.length > 0 ? [...prev, ...fresh] : prev;
};

export type UseIncomingLinksOptions = {
	initialPageSize?: number;
	loadMorePageSize?: number;
	/**
	 * Set to `false` to hold the fetch. Lets a caller that is mounted before its UI is
	 * reachable — the Teamwork Graph modal stays mounted while closed — keep the query
	 * off the view page until it is opened. Defaults to `true`.
	 */
	enabled?: boolean;
	/** Selects the graph node type and ARI the content is stored under. Undefined means page. */
	contentType?: BylineContentType;
};

export type UseIncomingLinksResult = {
	/** Every incoming link to list: graph rows followed by remote links. */
	links: EnhancedSmartLink[];
	/**
	 * Rows from the Teamwork Graph query. The byline's show/hide rule and icon stack read these
	 * alone: remote links cannot be server-side rendered and land after first paint, so letting
	 * them count would shift the page or repaint the trigger. CCINT-9346
	 */
	twgLinks: EnhancedSmartLink[];
	loading: boolean;
	/**
	 * True once nothing more can land without a `loadMore`: the graph rows have hydrated and the
	 * remote-link fallback has resolved, failed or timed out. Unlike `loading`, this waits for the
	 * fallback, so a count derived from `links` (the byline menu's "Appears in (N+)") does not change
	 * under the user. CCINT-9385
	 */
	settled: boolean;
	loadingMore: boolean;
	hasNextPage: boolean;
	endCursor: string | null;
	loadMore: () => void;
	error: ApolloErrorInterop | undefined;
};

const readPageInfo = (
	data: useIncomingLinksQuery$data | undefined,
): { hasNextPage: boolean; endCursor: string | null } => {
	const pageInfo = data?.graphStore_cypherQueryV2?.pageInfo;
	return {
		hasNextPage: pageInfo?.hasNextPage ?? false,
		endCursor: pageInfo?.endCursor ?? null,
	};
};

// `updateQuery` merge shared by both paged fetches (the initial catch-up loop and
// `loadMore`): append the incoming page's edges after the ones already in the cache
// entry, and adopt the new page's `pageInfo` so the next fetch reads the latest
// cursor. A response with no connection leaves the cache untouched.
const appendNextPage = (
	previousResult: useIncomingLinksQuery$data,
	fetchMoreResult: useIncomingLinksQuery$data | undefined,
): useIncomingLinksQuery$data => {
	const nextConnection = fetchMoreResult?.graphStore_cypherQueryV2;
	if (!nextConnection) {
		return previousResult;
	}
	const previousEdges = previousResult?.graphStore_cypherQueryV2?.edges ?? [];
	return {
		...previousResult,
		graphStore_cypherQueryV2: {
			...nextConnection,
			edges: [...previousEdges, ...nextConnection.edges],
			pageInfo: nextConnection.pageInfo,
		},
	};
};

/**
 * Fetches cross-product incoming links to a Confluence page (Jira issues,
 * Townsquare goals, other pages, etc. that reference it) from the Teamwork
 * Graph via the experimental AGG `graphStore_cypherQueryV2` field, then hydrates
 * each entity URL into an `EnhancedSmartLink` via ORS — the same resolution path
 * the outgoing links use. Returns a flat list.
 *
 * Must be rendered under a SmartCard provider (`useSmartLinkContext`).
 */
export const useIncomingLinks = (
	contentId: string | undefined,
	options?: UseIncomingLinksOptions,
): UseIncomingLinksResult => {
	const initialPageSize = options?.initialPageSize ?? DEFAULT_INITIAL_PAGE_SIZE;
	const loadMorePageSize = options?.loadMorePageSize ?? DEFAULT_LOAD_MORE_PAGE_SIZE;
	const enabled = options?.enabled ?? true;
	const contentType = options?.contentType;

	const { cloudId } = useSessionData();
	const { connections, store } = useSmartLinkContext();

	// `@confluence/analytics`, not the platform analytics-bridge helper. `fireOperationalAnalytics`
	// fires on the `analyticsBridge` channel, and `AnalyticsListener` only invokes its handler when
	// its own `channel` is `'*'` or equal to the event's; `ConfluenceAnalyticsListener` passes no
	// `channel`, so bridge events never reached it at all. See `.agents/incoming-links.md`. CCINT-9105
	const { createEvent } = useConfluenceAnalyticsEvents();
	const hasFiredRef = useRef(false);
	// Dedupes across pages so re-renders and merged-store updates never re-resolve a url.
	const knownUrlsRef = useRef<Set<string>>(new Set());
	const initialResolvedRef = useRef(false);

	// The SmartCard provider rebuilds its context value with `lodash.merge({}, ...)`, which mints a
	// new `store` object on every recompute even though the underlying Redux store is unchanged. We
	// read it through a ref and keep it out of the hydration callback's deps so an ancestor re-render
	// does not re-create the callback and re-fire the hydration effect.
	const storeRef = useRef(store);
	useEffect(() => {
		storeRef.current = store;
	}, [store]);

	const skip = !enabled || !contentId || !cloudId;

	const [links, setLinks] = useState<EnhancedSmartLink[]>([]);
	const [initialResolved, setInitialResolved] = useState(false);
	const [resolving, setResolving] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [remoteLinksResult, setRemoteLinksResult] = useState<RemoteLinksResult | null>(null);
	// Latches the merge so a graph refetch cannot un-merge the remote links or re-fire their event.
	const remoteLinksMergedRef = useRef(false);

	const reportError = useCallback(
		(err: ApolloErrorInterop) => {
			getMonitoringClient().submitError(err, { attribution: Attribution.INTEGRATIONS });
			markErrorAsHandled(err);
			createEvent(getIncomingLinksFailedEvent({ errorType: classifyError(err) })).fire();
		},
		[createEvent],
	);

	const {
		data,
		loading: queryLoading,
		error,
		fetchMore,
	} = useQueryInterop<useIncomingLinksQuery$data, useIncomingLinksQuery$variables>(
		IncomingLinksQuery,
		{
			variables: {
				cypherQuery: skip
					? ''
					: buildIncomingLinksCypher(buildContentAri(cloudId, contentId, contentType), contentType),
				first: initialPageSize,
				after: null,
			},
			skip,
			interopApolloAggClient: true,
			// Keep partial data on GraphQL errors. Hydrating cross-activation 3P
			// entities can raise a per-node error ("Cross activation id requests are
			// not allowed") while still returning the rest of the rows; with the
			// default 'none' policy that partial data is discarded before it reaches
			// us. 'all' surfaces `data` alongside `error` so we can render what came
			// back (see the effect below), and `onError` still reports the error.
			errorPolicy: 'all',
			context: skip ? undefined : { headers: { 'X-Query-Context': buildQueryContext(cloudId) } },
			onError: reportError,
		},
	);

	const { hasNextPage, endCursor } = readPageInfo(data);

	// Hydrate only the urls not seen before and append them in order. `knownUrlsRef`
	// is advanced only after a hydration commits, so a cancelled attempt (e.g.
	// StrictMode double-invoke) leaves those urls for the surviving attempt.
	const hydrateAndAppendPage = useCallback(
		async (urls: string[], isCancelled: () => boolean): Promise<number> => {
			const newUrls = urls.filter((url) => !knownUrlsRef.current.has(url));
			if (newUrls.length === 0) {
				return 0;
			}

			const hydrated = await hydrateIncomingLinks(newUrls, storeRef.current, connections.client);
			if (isCancelled()) {
				return 0;
			}

			newUrls.forEach((url) => knownUrlsRef.current.add(url));
			setLinks((prev) => appendNewLinks(prev, hydrated));
			return hydrated.length;
		},
		[connections.client],
	);

	// Hydrate the initial page once the query settles. We deliberately do NOT gate on
	// `error`: with `errorPolicy: 'all'` a partial cross-activation error still returns
	// usable rows, and bailing here would drop them (see the query's `errorPolicy`
	// note) — error *reporting* stays in `onError`. `initialResolvedRef` stops
	// loadMore's merged-store updates from re-running this initial hydration.
	useEffect(() => {
		if (skip || queryLoading || !data || initialResolvedRef.current) {
			return;
		}

		let cancelled = false;
		setResolving(true);

		// When the first graph page hydrates to 0 displayable links (common when
		// page-1 rows are uncatalogued or unresolvable), keep paging until we have
		// enough links to render the initial section or pagination is exhausted.
		const hydrateInitialPages = async () => {
			let resolvedCount = 0;
			let currentData: useIncomingLinksQuery$data = data;

			while (!cancelled) {
				resolvedCount += await hydrateAndAppendPage(
					readIncomingLinkUrls(currentData),
					() => cancelled,
				);
				const currentPageInfo = readPageInfo(currentData);

				if (cancelled || resolvedCount >= INITIAL_RESOLVED_LINK_TARGET) {
					break;
				}

				if (!currentPageInfo.hasNextPage || !currentPageInfo.endCursor) {
					break;
				}

				const nextPageResult = await fetchMore({
					variables: { first: loadMorePageSize, after: currentPageInfo.endCursor },
					updateQuery: (previousResult, { fetchMoreResult }) =>
						appendNextPage(previousResult, fetchMoreResult),
				});

				currentData = nextPageResult.data;
			}

			return resolvedCount;
		};

		hydrateInitialPages()
			.then((count) => {
				if (cancelled) {
					return;
				}
				initialResolvedRef.current = true;
				setInitialResolved(true);
				setResolving(false);
				if (!hasFiredRef.current) {
					hasFiredRef.current = true;
					createEvent(getIncomingLinksSucceededEvent({ count })).fire();
				}
			})
			.catch((err: unknown) => {
				// hydrateIncomingLinks/resolveSmartLinkByUrl swallow and report per-url
				// failures themselves; report unexpected pipeline rejections here and avoid
				// leaving `resolving` stuck. Mark the initial attempt resolved (even though it
				// failed) so the `initialResolutionPending` loading window closes and the effect
				// does not retry.
				if (!cancelled) {
					getMonitoringClient().submitError(err instanceof Error ? err : new Error(String(err)), {
						attribution: Attribution.INTEGRATIONS,
					});
					initialResolvedRef.current = true;
					setInitialResolved(true);
					setResolving(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [skip, queryLoading, data, loadMorePageSize, fetchMore, hydrateAndAppendPage, createEvent]);

	// Remote-linked Jira work items (see `fetchLinkedJiraWorkItemUrls`). Runs alongside the graph
	// query; a failure is reported here and never reaches the query's `error`. CCINT-9346
	useEffect(() => {
		if (skip || !contentId) {
			return;
		}

		let cancelled = false;
		let timedOut = false;
		const controller = new AbortController();
		const timer = setTimeout(() => {
			timedOut = true;
			controller.abort();
		}, REMOTE_LINKS_TIMEOUT_MS);

		fetchLinkedJiraWorkItemUrls(contentId, controller.signal)
			.then(async (urls) => {
				// The deadline covers the REST call only; hydration has ORS's own failure handling.
				clearTimeout(timer);
				return {
					fetched: urls.length,
					links:
						urls.length > 0
							? await hydrateIncomingLinks(urls, storeRef.current, connections.client)
							: [],
				};
			})
			.then(({ fetched, links: hydrated }) => {
				if (!cancelled) {
					setRemoteLinksResult({ status: 'resolved', fetched, links: hydrated });
				}
			})
			.catch((err: unknown) => {
				if (cancelled) {
					return;
				}
				// 4xx is expected for anonymous viewers and sites without a Jira app link, and a
				// timeout is our own deadline; neither is worth a monitoring report.
				if (!isLinkedJiraWorkItemsClientError(err) && !timedOut) {
					const error = err instanceof Error ? err : new Error(String(err));
					getMonitoringClient().submitError(error, { attribution: Attribution.INTEGRATIONS });
				}
				createEvent(
					getIncomingLinksRemoteLinksFailedEvent({
						errorType: classifyLinkedJiraWorkItemsError(err, timedOut),
					}),
				).fire();
				setRemoteLinksResult({ status: 'failed' });
			});

		return () => {
			cancelled = true;
			clearTimeout(timer);
			controller.abort();
		};
	}, [skip, contentId, connections.client, createEvent]);

	// Merge only once the graph has settled (initial hydration committed, or the query returned no
	// data) so its recency-ordered rows stay ahead of the appended remote links.
	const graphSettled = !skip && !queryLoading && (initialResolved || data == null);
	const remoteLinksMerged =
		remoteLinksResult != null && (graphSettled || remoteLinksMergedRef.current);

	useEffect(() => {
		if (!remoteLinksMerged || remoteLinksMergedRef.current) {
			return;
		}
		remoteLinksMergedRef.current = true;

		if (remoteLinksResult?.status === 'resolved') {
			createEvent(
				getIncomingLinksRemoteLinksSucceededEvent({
					fetched: remoteLinksResult.fetched,
					resolved: remoteLinksResult.links.length,
					added: appendNewLinks(links, remoteLinksResult.links).length - links.length,
				}),
			).fire();
		}
	}, [remoteLinksMerged, remoteLinksResult, links, createEvent]);

	// `links` state stays graph-only; the remote links are appended as a derived view so a later
	// graph page that ingests the same work item keeps the graph row and drops the remote copy.
	const mergedLinks = useMemo(
		() =>
			remoteLinksMerged && remoteLinksResult?.status === 'resolved'
				? appendNewLinks(links, remoteLinksResult.links)
				: links,
		[links, remoteLinksMerged, remoteLinksResult],
	);

	const loadMore = useCallback(() => {
		// `initialResolvedRef` gates against a click landing before the first page's
		// urls are committed to `knownUrlsRef`, which would re-resolve page 1 and
		// duplicate its links.
		if (skip || loadingMore || !hasNextPage || !endCursor || !initialResolvedRef.current) {
			return;
		}

		setLoadingMore(true);
		fetchMore({
			variables: { first: loadMorePageSize, after: endCursor },
			updateQuery: (previousResult, { fetchMoreResult }) =>
				appendNextPage(previousResult, fetchMoreResult),
		})
			.then((result) =>
				hydrateAndAppendPage(readIncomingLinkUrls(result.data), () => false).then((count) => {
					createEvent(getIncomingLinksLoadMoreSucceededEvent({ count })).fire();
				}),
			)
			// fetchMore errors surface via the query `onError`; just release the flag.
			.catch(() => {})
			.finally(() => setLoadingMore(false));
	}, [
		skip,
		loadingMore,
		hasNextPage,
		endCursor,
		loadMorePageSize,
		fetchMore,
		hydrateAndAppendPage,
		createEvent,
	]);

	// Stay `loading` from query start through the first hydration commit. `queryLoading || resolving`
	// alone leaves a gap: after the query settles (`queryLoading` → false) but before the hydration
	// effect sets `resolving`, one render commits with `loading` false and `links` still empty. An
	// inbound-only byline returns `null` on that render, so it flashes out and back in before the
	// resolved trigger appears. Deriving the pending window from `data`/`initialResolved` (both
	// available during render, no effect latency) closes the gap. CCINT-8932
	// Keep completion in state as well as the guard ref: an empty/cached page can resolve before
	// React commits `resolving: true`, and automatic batching can otherwise collapse the true/false
	// updates without producing the render that observes the updated ref. CCINT-9096
	const initialResolutionPending = !skip && data != null && !initialResolved;

	// `loading` deliberately ignores the remote-link fallback: nothing painted depends on it.
	// `settled` is the flag for the one reader that does (the menu's section count).
	const loading = queryLoading || resolving || initialResolutionPending;
	return {
		links: mergedLinks,
		twgLinks: links,
		loading,
		settled: skip || (!loading && remoteLinksMerged),
		loadingMore,
		hasNextPage,
		endCursor,
		loadMore,
		error,
	};
};
